import type {
    EntityPatch,
    HistoryEntitySnapshot,
} from '../types';
import type {
    HistoryDomainAdapter,
    HistoryPatchApplyInput,
} from '../domain/HistoryDomainAdapter';
import type {SketchSolverIslandSnapshot} from './SketchSolverRecordTypes';
import {
    areSketchSolverIslandSnapshotsEqual,
    isSketchSolverIslandSnapshot,
} from './SolverIslandSnapshot';

/**
 * solver island 历史 adapter ID。
 */
export const SKETCH_SOLVER_HISTORY_ADAPTER_ID = 'history-domain:sketch-solver-island';

/**
 * solver island 历史运行时存储。
 *
 * 设计说明：
 * 真实实现必须写入 CAD 主状态的 solver island 快照表，而不是写入 Three.js 对象或渲染缓存。
 */
export interface SketchSolverHistoryStore {
    /**
     * 写入 solver island 快照。
     *
     * @param snapshot solver island 快照。
     */
    readonly upsertSketchSolverIsland: (snapshot: SketchSolverIslandSnapshot) => void;
    /**
     * 删除 solver island 快照。
     *
     * @param islandId solver island ID。
     */
    readonly deleteSketchSolverIsland: (islandId: string) => void;
}

/**
 * 判断 solver island 快照是否为可持久化主状态。
 *
 * @param snapshot solver island 快照。
 * @returns 快照是否合法。
 */
export function isPlainSketchSolverIslandSnapshot(snapshot: SketchSolverIslandSnapshot): boolean {
    return isSketchSolverIslandSnapshot(snapshot);
}

/**
 * 应用 solver island 快照。
 *
 * @param input 补丁应用输入。
 */
function applySketchSolverSnapshot(
    input: HistoryPatchApplyInput<SketchSolverIslandSnapshot, SketchSolverHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.deleteSketchSolverIsland(input.patch.entityId);

        return;
    }

    if (!snapshot.data) {
        throw new Error(`solver island ${input.patch.entityId} 缺少快照数据。`);
    }

    if (!isPlainSketchSolverIslandSnapshot(snapshot.data)) {
        throw new Error(`solver island ${input.patch.entityId} 的快照不是可序列化主状态。`);
    }

    input.context.upsertSketchSolverIsland(snapshot.data);
}

/**
 * 比较 solver island 快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 快照是否语义相等。
 */
function areSolverIslandHistorySnapshotsEqual(
    left: HistoryEntitySnapshot<SketchSolverIslandSnapshot>,
    right: HistoryEntitySnapshot<SketchSolverIslandSnapshot>,
): boolean {
    if (left.exists !== right.exists) {
        return false;
    }

    if (!left.exists || !right.exists) {
        return true;
    }

    if (!left.data || !right.data) {
        return false;
    }

    return areSketchSolverIslandSnapshotsEqual(left.data, right.data);
}

/**
 * 创建 solver island 历史 adapter。
 *
 * @returns solver island 历史 adapter。
 */
export function createSketchSolverHistoryAdapter(): HistoryDomainAdapter<
    SketchSolverIslandSnapshot,
    SketchSolverHistoryStore
> {
    return {
        adapterId: SKETCH_SOLVER_HISTORY_ADAPTER_ID,
        kind: 'solver-island',
        applySnapshot: applySketchSolverSnapshot,
        areSnapshotsEqual: areSolverIslandHistorySnapshotsEqual as HistoryDomainAdapter<
            SketchSolverIslandSnapshot,
            SketchSolverHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建 solver island 历史补丁。
 *
 * @param patch 补丁字段。
 * @returns solver island 历史补丁。
 */
export function createSketchSolverIslandHistoryPatch(
    patch: Omit<EntityPatch<SketchSolverIslandSnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<SketchSolverIslandSnapshot> {
    return {
        ...patch,
        entityKind: 'solver-island',
        adapterId: SKETCH_SOLVER_HISTORY_ADAPTER_ID,
    };
}
